import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, LayoutDashboard, IdCard, Send, ShieldCheck, Users2, Activity, ExternalLink,
  Copy, CheckCircle2, XCircle, Pause, Play, Trash2, KeyRound, RefreshCw, Search, UserPlus, X, Crown, Eye, Smartphone, Monitor, Mail, AlertTriangle, Hourglass,
} from "lucide-react";
import { portalApi, portalToken, formatApiError } from "./api";
import { BRAND } from "@/data/brand";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "cards", label: "Digital Cards", icon: IdCard },
  { key: "reviews", label: "Pending Reviews", icon: Hourglass },
  { key: "invitations", label: "Invitations", icon: Send },
  { key: "licenses", label: "License Verification", icon: ShieldCheck },
  { key: "team", label: "Administrators", icon: Crown, superOnly: true },
  { key: "activity", label: "Activity", icon: Activity },
];

export default function PortalAdmin() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const nav = useNavigate();

  useEffect(() => {
    portalApi.me().then(({ user }) => setMe(user)).catch(() => nav("/digital-card/login"));
  }, [nav]);

  const logout = () => { portalToken.clear(); nav("/digital-card/login"); };
  if (!me) return <main className="min-h-screen bg-midnight-deeper flex items-center justify-center text-ag-text-muted font-mono text-xs tracking-[0.3em]">LOADING…</main>;

  const isSuper = me.role === "super_admin";
  const visibleTabs = TABS.filter((t) => !t.superOnly || isSuper);

  return (
    <main data-testid="portal-admin" className="min-h-screen bg-midnight-deeper">
      {/* Top bar */}
      <header className="border-b border-white/8 px-4 sm:px-8 py-4 flex items-center justify-between sticky top-0 z-30 bg-midnight-deeper/95 backdrop-blur">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logoWhite} alt="AG Public Adjusting" className="h-9 w-auto object-contain" />
          <div className="hidden sm:block leading-tight border-l border-white/10 pl-3">
            <div className="font-mono text-[0.55rem] text-gold/80 tracking-[0.32em] uppercase">Admin CRM</div>
            <div className="font-mono text-[0.5rem] mt-1 text-ag-text-muted tracking-[0.3em] uppercase">{isSuper ? "Super Admin" : "Admin"}</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <span className="hidden md:inline text-xs text-ag-text-muted font-mono truncate max-w-[200px]">{me.email}</span>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-xs font-mono tracking-[0.22em] uppercase">
            <LogOut className="w-3.5 h-3.5" /> Log Out
          </button>
        </div>
      </header>

      {/* Tabs — horizontal on desktop, scrollable on mobile */}
      <nav className="border-b border-white/8 px-2 sm:px-8 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {visibleTabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-mono tracking-[0.22em] uppercase whitespace-nowrap border-b-2 transition ${tab === key ? "text-gold border-gold" : "text-white/50 border-transparent hover:text-white/80"}`}
            ><Icon className="w-3.5 h-3.5" /> {label}</button>
          ))}
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        {tab === "dashboard" && <Dashboard />}
        {tab === "cards" && <CardsTab me={me} />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "invitations" && <InvitationsTab me={me} />}
        {tab === "licenses" && <LicensesTab me={me} />}
        {tab === "team" && isSuper && <TeamTab me={me} />}
        {tab === "activity" && <ActivityTab />}
      </div>
    </main>
  );
}

// ═══════════════ DASHBOARD ═══════════════
function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { portalApi.admin.dashboard().then(setData).catch(() => {}); }, []);
  if (!data) return <div className="text-ag-text-muted text-xs font-mono">LOADING…</div>;
  const { totals, metrics } = data;
  const stats = [
    { label: "Total Digital Cards", value: totals.total_cards },
    { label: "Active Cards", value: totals.active_cards },
    { label: "Pending Reviews", value: totals.pending_reviews || 0, accent: (totals.pending_reviews || 0) > 0 },
    { label: "Pending Invitations", value: totals.pending_invitations },
    { label: "Pending License Reviews", value: totals.pending_licenses, accent: true },
    { label: "Suspended Users", value: totals.suspended_users },
    { label: "Total Administrators", value: totals.total_administrators },
    { label: "Cards This Month", value: totals.cards_this_month },
  ];
  const mtx = [
    { label: "Card Views", value: metrics.views },
    { label: "QR Opens", value: metrics.qr_opens },
    { label: "Save Contact", value: metrics.save_contacts },
    { label: "Call Clicks", value: metrics.call_clicks },
    { label: "Email Clicks", value: metrics.email_clicks },
    { label: "Text Clicks", value: metrics.text_clicks },
  ];
  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      <section>
        <h2 className="font-display text-xl text-white mb-6 font-light tracking-[-0.02em]">Overview</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div key={s.label} className={`glass p-5 ${s.accent ? "border-gold/40" : ""}`} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g,"-")}`}>
              <div className="font-mono text-[0.55rem] text-ag-text-muted tracking-[0.28em] uppercase mb-2">{s.label}</div>
              <div className={`font-display-tight text-3xl leading-none ${s.accent ? "gold-metallic" : "text-white"}`}>{s.value}</div>
            </div>
          ))}
        </div>
      </section>
      <section>
        <h2 className="font-display text-xl text-white mb-6 font-light tracking-[-0.02em]">Engagement</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {mtx.map((s) => (
            <div key={s.label} className="glass p-5">
              <div className="font-mono text-[0.55rem] text-ag-text-muted tracking-[0.28em] uppercase mb-2">{s.label}</div>
              <div className="font-display-tight text-2xl text-white leading-none">{s.value}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ═══════════════ DIGITAL CARDS ═══════════════
function CardsTab({ me }) {
  const [adjusters, setAdjusters] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const load = () => portalApi.admin.listAdjusters().then((r) => setAdjusters(r.adjusters)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const qq = q.toLowerCase().trim();
    return adjusters.filter((a) => {
      if (filter === "active" && !(a.published && !a.suspended)) return false;
      if (filter === "pending" && a.license_status !== "pending") return false;
      if (filter === "suspended" && !a.suspended) return false;
      if (filter === "verified" && a.license_status !== "verified") return false;
      if (filter === "draft" && (a.published || a.suspended)) return false;
      if (qq) {
        const hay = `${a.first_name} ${a.last_name} ${a.email} ${a.license_number || ""}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      return true;
    });
  }, [adjusters, q, filter]);

  return (
    <div data-testid="admin-cards-tab">
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input placeholder="Search by name, email, license #" value={q} onChange={(e) => setQ(e.target.value)} className="ag-input !py-3 !pl-10" data-testid="cards-search" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="ag-input !py-3 sm:w-56" data-testid="cards-filter">
          <option value="all">All</option>
          <option value="active">Active (Published)</option>
          <option value="draft">Draft</option>
          <option value="pending">License Pending</option>
          <option value="verified">License Verified</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>
      <UserList users={filtered} me={me} onChanged={load} />
    </div>
  );
}

function UserList({ users, me, onChanged }) {
  const isSuper = me.role === "super_admin";
  const [previewSlug, setPreviewSlug] = useState(null);
  const doVerify = async (id, status) => { try { await portalApi.admin.verify(id, status); toast.success(`Status: ${status}`); onChanged(); } catch (e) { toast.error(formatApiError(e)); } };
  const doSuspend = async (id) => { try { const r = await portalApi.admin.suspend(id); toast.success(r.suspended ? "Suspended" : "Reactivated"); onChanged(); } catch (e) { toast.error(formatApiError(e)); } };
  const doReset = async (id) => { try { const { temporary_password } = await portalApi.admin.resetPassword(id); navigator.clipboard.writeText(temporary_password); toast.success(`Temp password copied: ${temporary_password}`); } catch (e) { toast.error(formatApiError(e)); } };
  const doDelete = async (id) => { if (!window.confirm("Permanently delete this account?")) return; try { await portalApi.admin.remove(id); toast.success("Deleted"); onChanged(); } catch (e) { toast.error(formatApiError(e)); } };
  const doPromote = async (id, role) => { try { await portalApi.admin.changeRole(id, role); toast.success(`Role: ${role}`); onChanged(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-3">
      {users.map((a) => (
        <div key={a.id} className="glass p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-row-${a.id}`}>
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {a.photo_url ? <img src={a.photo_url} alt="" className="w-12 h-12 rounded-full object-cover border border-gold/30" /> : <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10" />}
            <div className="min-w-0">
              <div className="font-display text-lg text-white font-light tracking-[-0.02em] truncate">{a.first_name} {a.last_name}</div>
              <div className="text-xs text-ag-text-muted truncate">{a.email}{a.phone ? ` · ${a.phone}` : ""}</div>
              <div className="mt-2 flex flex-wrap gap-1.5 items-center text-[0.55rem] font-mono">
                <Badge tone="role">{a.role.toUpperCase()}</Badge>
                <Badge tone={a.license_status === "verified" ? "green" : a.license_status === "invalid" ? "red" : a.license_status === "not_submitted" ? "muted" : "yellow"}>LIC: {(a.license_status || "").toUpperCase() || "—"}</Badge>
                {a.license_number && <Badge tone="muted">#{a.license_number}</Badge>}
                <Badge tone={a.published ? "gold" : "muted"}>{a.published ? "PUBLISHED" : "DRAFT"}</Badge>
                {a.suspended && <Badge tone="red">SUSPENDED</Badge>}
                {a.slug && <a href={`/card/${a.slug}`} target="_blank" rel="noopener noreferrer" className="border border-white/10 text-white/60 hover:text-gold inline-flex items-center gap-1 px-2 py-1">/{a.slug} <ExternalLink className="w-2.5 h-2.5" /></a>}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {a.slug && (
              <button onClick={() => setPreviewSlug(a.slug)} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1 !border-gold/40 !text-gold" data-testid={`admin-preview-${a.id}`}><Eye className="w-3 h-3" /> Preview</button>
            )}
            <a href="https://myfloridacfo.com/division/agents/licensee-search" target="_blank" rel="noopener noreferrer" className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Lookup</a>
            <button onClick={() => doVerify(a.id, "verified")} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1" data-testid={`admin-verify-${a.id}`}><CheckCircle2 className="w-3 h-3 text-emerald-300" /> Verify</button>
            <button onClick={() => doVerify(a.id, "invalid")} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1"><XCircle className="w-3 h-3 text-red-300" /> Reject</button>
            <button onClick={() => doSuspend(a.id)} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1" data-testid={`admin-suspend-${a.id}`}>{a.suspended ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />} {a.suspended ? "Unsuspend" : "Suspend"}</button>
            <button onClick={() => doReset(a.id)} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> Reset</button>
            {isSuper && a.role !== "super_admin" && (
              <button onClick={() => doPromote(a.id, a.role === "adjuster" ? "admin" : "super_admin")} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1" data-testid={`admin-promote-${a.id}`}><Crown className="w-3 h-3 text-gold" /> {a.role === "adjuster" ? "Make Admin" : "Make Super"}</button>
            )}
            {isSuper && a.role !== "adjuster" && a.id !== me.id && (
              <button onClick={() => doPromote(a.id, "adjuster")} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1">Demote</button>
            )}
            <button onClick={() => doDelete(a.id)} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1 hover:border-red-400/40"><Trash2 className="w-3 h-3 text-red-300" /></button>
          </div>
        </div>
      ))}
      {users.length === 0 && <div className="text-ag-text-muted text-sm text-center py-10">No users match.</div>}
      {previewSlug && <CardPreviewModal slug={previewSlug} onClose={() => setPreviewSlug(null)} />}
    </div>
  );
}

// ═══════════════ CARD PREVIEW MODAL ═══════════════
function CardPreviewModal({ slug, onClose }) {
  const [device, setDevice] = useState("mobile"); // mobile | desktop
  const url = `/card/${slug}?adminPreview=1`;
  const publicUrl = `${window.location.origin}/card/${slug}`;
  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success("Public link copied"); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex flex-col" data-testid="card-preview-modal">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-white/10 px-4 sm:px-8 py-4">
        <div>
          <div className="font-mono text-[0.55rem] text-gold tracking-[0.32em] uppercase mb-1">Client Preview</div>
          <div className="font-display text-white text-lg font-light tracking-[-0.02em] truncate max-w-[60vw]">/card/{slug}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center border border-white/10 rounded-full p-1">
            <button
              onClick={() => setDevice("mobile")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.6rem] font-mono tracking-[0.22em] uppercase transition ${device === "mobile" ? "bg-gold/20 text-gold" : "text-white/50 hover:text-white"}`}
              data-testid="preview-device-mobile"
            ><Smartphone className="w-3 h-3" /> Mobile</button>
            <button
              onClick={() => setDevice("desktop")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.6rem] font-mono tracking-[0.22em] uppercase transition ${device === "desktop" ? "bg-gold/20 text-gold" : "text-white/50 hover:text-white"}`}
              data-testid="preview-device-desktop"
            ><Monitor className="w-3 h-3" /> Desktop</button>
          </div>
          <button onClick={copyLink} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1" data-testid="preview-copy-link"><Copy className="w-3 h-3" /> Copy Link</button>
          <a href={url} target="_blank" rel="noopener noreferrer" className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1" data-testid="preview-open-new"><ExternalLink className="w-3 h-3" /> Open</a>
          <button onClick={onClose} className="text-white/60 hover:text-gold p-2" data-testid="preview-close"><X className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Stage */}
      <div className="flex-1 overflow-auto flex items-start justify-center py-8 px-4">
        {device === "mobile" ? (
          <div className="relative" style={{ width: 390 }}>
            {/* Phone frame */}
            <div className="relative rounded-[42px] border-[10px] border-neutral-800 bg-neutral-900 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)] overflow-hidden" style={{ height: 800 }}>
              <div className="absolute top-0 inset-x-0 h-6 flex items-center justify-center bg-neutral-900 z-10">
                <div className="w-24 h-4 bg-black rounded-b-2xl" />
              </div>
              <iframe
                src={url}
                title="Client preview"
                className="w-full h-full border-0 bg-midnight-deeper"
                data-testid="preview-iframe"
              />
            </div>
            <div className="mt-4 text-center font-mono text-[0.55rem] text-ag-text-muted tracking-[0.28em] uppercase">iPhone · 390 × 800</div>
          </div>
        ) : (
          <div className="w-full max-w-5xl">
            <div className="relative rounded-lg border border-white/10 bg-midnight-deeper overflow-hidden shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]" style={{ height: "75vh" }}>
              <iframe
                src={url}
                title="Client preview"
                className="w-full h-full border-0 bg-midnight-deeper"
                data-testid="preview-iframe"
              />
            </div>
            <div className="mt-4 text-center font-mono text-[0.55rem] text-ag-text-muted tracking-[0.28em] uppercase">Desktop preview</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Badge({ tone, children }) {
  const tones = {
    green: "border-emerald-400/40 text-emerald-300",
    red: "border-red-400/40 text-red-300",
    yellow: "border-yellow-300/40 text-yellow-200",
    gold: "border-gold/40 text-gold",
    muted: "border-white/10 text-white/50",
    role: "border-glow/30 text-glow",
  };
  return <span className={`px-2 py-1 border ${tones[tone] || tones.muted} tracking-[0.24em] uppercase`}>{children}</span>;
}

// ═══════════════ INVITATIONS ═══════════════
function InvitationsTab({ me }) {
  const [invitations, setInvitations] = useState([]);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", role: "adjuster" });
  const [emailConfigured, setEmailConfigured] = useState(null); // null = loading, bool otherwise
  const load = () => portalApi.admin.listInvitations().then((r) => setInvitations(r.invitations)).catch(() => {});
  useEffect(() => {
    load();
    portalApi.admin.emailStatus().then((r) => setEmailConfigured(!!r.configured)).catch(() => setEmailConfigured(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { invitation, email } = await portalApi.admin.createInvitation(form.email, form.first_name, form.last_name, form.role);
      navigator.clipboard.writeText(invitation.invite_url);
      if (email?.sent) toast.success(`Email sent to ${invitation.email} · Link also copied`);
      else toast.success("Invitation link created & copied (email not configured)");
      setForm({ email: "", first_name: "", last_name: "", role: "adjuster" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("Link copied"); };
  const regen = async (id) => { try { const { invitation, email } = await portalApi.admin.regenerateInvitation(id); navigator.clipboard.writeText(invitation.invite_url); toast.success(email?.sent ? "New link sent by email & copied" : "New link copied"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const resendEmail = async (id) => { try { await portalApi.admin.resendInvitationEmail(id); toast.success("Email sent"); } catch (e) { toast.error(formatApiError(e)); } };
  const revoke = async (id) => { try { await portalApi.admin.revokeInvitation(id); toast.success("Revoked"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  const isSuper = me.role === "super_admin";

  return (
    <div data-testid="admin-invitations-tab" className="space-y-8">
      {emailConfigured === false && (
        <div className="glass p-4 border-yellow-400/30 flex items-start gap-3" data-testid="email-not-configured-banner">
          <AlertTriangle className="w-4 h-4 text-yellow-300 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-yellow-100/90 font-light leading-relaxed">
            <strong className="font-mono tracking-[0.22em] uppercase text-yellow-200 text-[0.65rem]">Email Delivery Off</strong>
            <div className="mt-1">Set <span className="font-mono text-gold">RESEND_API_KEY</span> and <span className="font-mono text-gold">RESEND_FROM_EMAIL</span> in <span className="font-mono">backend/.env</span> and restart the backend to enable automatic email invitations. Meanwhile, invitations still work — the secure link is copied to your clipboard.</div>
          </div>
        </div>
      )}
      <section className="glass-strong p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <UserPlus className="w-4 h-4 text-gold" />
          <h2 className="font-display text-xl text-white font-light tracking-[-0.02em]">Create Invitation</h2>
          {emailConfigured && <span className="ml-auto inline-flex items-center gap-1.5 text-emerald-300 text-[0.55rem] font-mono tracking-[0.22em] uppercase"><Mail className="w-3 h-3" /> Email On</span>}
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input className="ag-input sm:col-span-2" placeholder="email@example.com" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inv-email" />
          <input className="ag-input" placeholder="First name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="inv-first" />
          <input className="ag-input" placeholder="Last name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="inv-last" />
          {isSuper && (
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="ag-input sm:col-span-4" data-testid="inv-role">
              <option value="adjuster">Adjuster</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          )}
          <button type="submit" className="btn-gold sm:col-span-4 !py-3 !text-[0.62rem]" data-testid="inv-submit">Create Invitation{emailConfigured ? " & Send Email" : ""}</button>
        </form>
        <p className="mt-4 text-[0.7rem] text-ag-text-muted font-light">
          {emailConfigured
            ? "The invitation will be emailed automatically to the adjuster. The secure link is also copied to your clipboard as a backup."
            : "The invitation link will be copied to your clipboard. Paste it into email, SMS, or WhatsApp to share with the adjuster."}
        </p>
      </section>

      <section>
        <h2 className="font-display text-xl text-white font-light tracking-[-0.02em] mb-6">All Invitations ({invitations.length})</h2>
        <div className="space-y-3">
          {invitations.map((inv) => {
            const used = !!inv.used_at, expired = new Date(inv.expires_at) < new Date();
            const state = inv.revoked ? "REVOKED" : used ? "USED" : expired ? "EXPIRED" : "PENDING";
            const canResend = !used && !inv.revoked && !expired;
            return (
              <div key={inv.id} className="glass p-5 flex flex-col md:flex-row md:items-center gap-3" data-testid={`inv-row-${inv.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base text-white font-light truncate">{inv.first_name} {inv.last_name}</div>
                  <div className="text-xs text-ag-text-muted truncate">{inv.email}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5 items-center text-[0.55rem] font-mono">
                    <Badge tone="role">{inv.role.toUpperCase()}</Badge>
                    <Badge tone={state === "PENDING" ? "gold" : state === "USED" ? "green" : "muted"}>{state}</Badge>
                  </div>
                  {canResend && (
                    <div className="mt-3 flex items-center gap-2 text-[0.65rem] font-mono text-white/60 break-all">
                      <span className="text-gold/70">LINK:</span><span className="truncate">{inv.invite_url}</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {canResend && emailConfigured && (
                    <button onClick={() => resendEmail(inv.id)} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1 !border-emerald-300/40 !text-emerald-200" data-testid={`inv-resend-email-${inv.id}`}><Mail className="w-3 h-3" /> Resend Email</button>
                  )}
                  {canResend && (
                    <button onClick={() => copy(inv.invite_url)} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                  )}
                  <button onClick={() => regen(inv.id)} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Regen</button>
                  {!inv.revoked && !used && <button onClick={() => revoke(inv.id)} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1"><X className="w-3 h-3" /> Revoke</button>}
                </div>
              </div>
            );
          })}
          {invitations.length === 0 && <div className="text-ag-text-muted text-sm text-center py-10">No invitations yet.</div>}
        </div>
      </section>
    </div>
  );
}

// ═══════════════ LICENSE QUEUE ═══════════════
function LicensesTab({ me }) {
  const [adjusters, setAdjusters] = useState([]);
  const load = () => portalApi.admin.listAdjusters().then((r) => setAdjusters(r.adjusters)).catch(() => {});
  useEffect(() => { load(); }, []);
  const queue = adjusters.filter((a) => ["pending", "requires_review", "invalid"].includes(a.license_status));
  return (
    <div data-testid="admin-licenses-tab">
      <h2 className="font-display text-xl text-white font-light tracking-[-0.02em] mb-6">License Verification Queue ({queue.length})</h2>
      <UserList users={queue} me={me} onChanged={load} />
    </div>
  );
}

// ═══════════════ TEAM (SUPER ADMIN ONLY) ═══════════════
function TeamTab({ me }) {
  const [team, setTeam] = useState([]);
  const load = () => portalApi.admin.listTeam().then((r) => setTeam(r.team)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-team-tab">
      <h2 className="font-display text-xl text-white font-light tracking-[-0.02em] mb-6">Administrators ({team.length})</h2>
      <p className="text-ag-text-secondary text-sm font-light mb-6">Use the Invitations tab to invite a new Admin or Super Admin. Promote/demote below.</p>
      <UserList users={team} me={me} onChanged={load} />
    </div>
  );
}

// ═══════════════ ACTIVITY ═══════════════
function ActivityTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { portalApi.admin.activity(100).then((r) => setItems(r.activity)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-activity-tab">
      <h2 className="font-display text-xl text-white font-light tracking-[-0.02em] mb-6">Recent Activity</h2>
      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.id} className="glass p-4 flex items-center gap-4 text-sm">
            <div className="font-mono text-[0.55rem] text-gold tracking-[0.22em] uppercase min-w-[130px]">{a.action.replace(/_/g, " ")}</div>
            <div className="flex-1 min-w-0 text-white/90 font-light truncate">
              {a.actor_name && <span>{a.actor_name}</span>}
              {a.subject_name && a.subject_name !== a.actor_name && <span className="text-white/50"> → {a.subject_name}</span>}
              {a.meta?.email && <span className="text-white/50"> · {a.meta.email}</span>}
              {a.meta?.new_role && <span className="text-white/50"> · {a.meta.new_role}</span>}
            </div>
            <div className="font-mono text-[0.55rem] text-ag-text-muted whitespace-nowrap">{new Date(a.at).toLocaleString()}</div>
          </div>
        ))}
        {items.length === 0 && <div className="text-ag-text-muted text-sm text-center py-10">No activity yet.</div>}
      </div>
    </div>
  );
}


// ═══════════════ PENDING REVIEWS ═══════════════
function ReviewsTab() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    portalApi.admin.listPendingChanges()
      .then((r) => setPending(r.pending))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await portalApi.admin.approveChanges(id); toast.success("Changes approved & applied"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    if (!window.confirm("Reject these proposed changes? The adjuster will need to resubmit.")) return;
    try { await portalApi.admin.rejectChanges(id); toast.success("Changes rejected"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-reviews-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl text-white font-light tracking-[-0.02em]">Pending Card Changes ({pending.length})</h2>
        <button onClick={load} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1" data-testid="reviews-refresh"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>
      <p className="text-ag-text-secondary text-sm font-light mb-6">Adjusters can edit their published card. Changes appear here for approval before going live on the public page.</p>

      {loading ? (
        <div className="text-ag-text-muted text-xs font-mono">LOADING…</div>
      ) : pending.length === 0 ? (
        <div className="glass p-8 text-center text-ag-text-muted text-sm">No pending reviews. All caught up.</div>
      ) : (
        <div className="space-y-4">
          {pending.map((p) => (
            <div key={p.id} className="glass p-5" data-testid={`review-row-${p.id}`}>
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {p.photo_url ? <img src={p.photo_url} alt="" className="w-12 h-12 rounded-full object-cover border border-gold/30" /> : <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10" />}
                  <div className="min-w-0">
                    <div className="font-display text-lg text-white font-light truncate">{p.first_name} {p.last_name}</div>
                    <div className="text-xs text-ag-text-muted truncate">{p.email}</div>
                    {p.submitted_at && <div className="mt-1 font-mono text-[0.55rem] text-gold/70 tracking-[0.22em] uppercase">Submitted {new Date(p.submitted_at).toLocaleString()}</div>}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {p.slug && <a href={`/card/${p.slug}?adminPreview=1`} target="_blank" rel="noopener noreferrer" className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Preview</a>}
                  <button onClick={() => approve(p.id)} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1 !border-emerald-300/40 !text-emerald-200" data-testid={`review-approve-${p.id}`}><CheckCircle2 className="w-3 h-3" /> Approve</button>
                  <button onClick={() => reject(p.id)} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem] inline-flex items-center gap-1 !border-red-400/40 !text-red-300" data-testid={`review-reject-${p.id}`}><XCircle className="w-3 h-3" /> Reject</button>
                </div>
              </div>
              <div className="mt-4 border-t border-white/8 pt-4 grid gap-2">
                {Object.entries(p.diff).map(([field, { current, proposed }]) => (
                  <div key={field} className="grid grid-cols-1 sm:grid-cols-[120px_1fr_1fr] gap-2 text-xs">
                    <div className="font-mono text-[0.6rem] text-gold/80 tracking-[0.22em] uppercase self-center">{field}</div>
                    <div className="text-white/40 line-through break-all">{formatDiffValue(current) || <span className="italic opacity-60">— empty —</span>}</div>
                    <div className="text-white break-all">{formatDiffValue(proposed) || <span className="italic opacity-60">— empty —</span>}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDiffValue(v) {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
